<template>
  <v-responsive min-width="100" max-width="250">
    <v-autocomplete
      :key="placeholder"
      v-model="value"
      :label="label"
      density="compact"
      :required="required"
      :disabled="disabled || loading"
      :placeholder="placeholder"
      :items="transformedOptions"
      clearable
      hide-details
      @click:clear="clearAction"
      @update:model-value="applyFilter"
    ></v-autocomplete>
  </v-responsive>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { apiClientV2, Context } from '@/api/ApiClientV2'
import { i18n } from '@/lang/setup'
import { TransientBaseObjectType } from '@/models/core/base'
import { getDependencies } from '@/models/objectRegistry'
import routerHandler from '@/router/routerHandler'

type ParentSelectorsV2Props = {
  modelClass: TransientBaseObjectType
  required?: boolean
  isForm?: boolean
  customDisplayName?: (option: any) => Promise<void>
  filterOption?: (option: any) => boolean
  sortOptions?: (a: any, b: any) => number
  disabled?: boolean
  orderBy?: string
}

const route = useRoute()
const store = useStore()

const props = withDefaults(defineProps<ParentSelectorsV2Props>(), {
  required: false,
  isForm: false,
  customDisplayName: null,
  filterOption: null,
  sortOptions: null,
  disabled: false,
  orderBy: null,
})

const label = ref('')
const value = ref(null)
const placeholder = ref('')
const loading = ref(true)
const options = ref([])
const prefix = ref('')
const ancestorObjectTypes = ref<string[]>([])

watch(
  () => route.query,
  () => {
    getOptions()
  }
)

const clearAction = () => applyFilter('')

const transformedOptions = computed(() => {
  return options.value.map((option) => {
    return {
      ...option,
      value: option.id ?? '',
      title: option._displayName,
    }
  })
})

function displayName(option): string {
  // TODO: This is not really good
  if ('name' in option) {
    return option.name
  } else if ('handle' in option) {
    return option.handle
  } else if ('title' in option) {
    return option.title
  } else {
    return ''
  }
}

onMounted(() => {
  label.value = props.modelClass.prettyName() || ''
  ancestorObjectTypes.value = props.modelClass.ancestors.map(
    (ancestor) => ancestor.modelClass.objectType
  )
  getOptions()
})

async function getOptions() {
  loading.value = true
  placeholder.value = i18n.global.t('components.common.loading') + '...'

  let deselectedText = `- ${i18n.global.t('components.common.noFilter')} -`
  if (props.isForm) {
    deselectedText = `- ${i18n.global.t('components.common.noSelection')} -`
  }
  const filter = routerHandler.query(props.prefix)
  // remove filters that are not ancestors
  Object.keys(filter).forEach((key: string) => {
    if (!ancestorObjectTypes.value.find((objectType) => key === objectType)) {
      delete filter[key]
    }
  })
  filter.organisation = store.getters['global/organisation'].id
  const context: Context = {
    filter: filter,
    pagination: {
      page: 1,
      pageSize: 100,
    },
  }
  if (props.orderBy) {
    context.filter.order_by = props.orderBy
  }
  // empty options already here to not show old options
  options.value = []
  try {
    const results = await apiClientV2.getListItems<any>(
      props.modelClass,
      context
    )
    // empty again to avoid duplicates if getOptions is called multiple times
    options.value = []
    if (!props.required) {
      options.value.push({
        name: deselectedText,
      })
    }
    options.value = options.value.concat(results)
    if (props.customDisplayName) {
      const promises = []
      options.value.forEach((option) => {
        promises.push(props.customDisplayName(option))
      })
      await Promise.all(promises)
    } else {
      options.value.forEach((option) => {
        option._displayName = displayName(option)
      })
    }
    if (props.filterOption) {
      options.value = options.value.filter(props.filterOption)
    }
    if (props.sortOptions) {
      options.value.sort(props.sortOptions)
    }
    if (options.value.length === 0) {
      placeholder.value = `${i18n.global.t('components.common.no')} ${
        label.value
      } ${i18n.global.t('components.common.found')}`
    } else {
      if (props.isForm) {
        placeholder.value = `${label.value} ${i18n.global.t(
          'components.common.select'
        )}`
      } else {
        placeholder.value = `${i18n.global.t('components.common.filterBy')} ${
          label.value
        }`
      }
    }
    // select option in url
    value.value = routerHandler.query(prefix.value)[props.modelClass.objectType]
    // if only one option available, select this option
    if (options.value.length === 1) {
      value.value = options.value[0].id
      applyFilter(value.value)
    }
  } catch (err) {
    options.value = []
    placeholder.value = `No ${label.value} found`
    console.warn(err)
  }
  loading.value = false
}

function applyFilter(id: string) {
  // only apply if filter changed
  if (
    id !== null &&
    routerHandler.query(prefix.value)[props.modelClass.objectType] !== id
  ) {
    const pagination = props.modelClass.defaultPagination || {}

    const redirect = {
      path: route.path,
      query: {
        ...route.query,
        [props.modelClass.objectType]: id,
        ...(pagination as any),
      },
    }

    // clear selected page when changing a filter
    delete redirect.query.page

    for (const item of getDependencies(props.modelClass.objectType)) {
      // delete depending object query parameters
      // e.g. when product changes, model must be cleared
      delete redirect.query[item]
    }
    routerHandler.push(redirect)
  }
}
</script>

<style lang="scss"></style>
